import { useState, useEffect, useRef } from "react";
import { X, Circle, Clock, CheckCircle2, UserX, Loader2, Trash2, Plus } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { avisarSeDiariaFalhou } from "@/hooks/use-equipe";
import { isSemQuinzena, SEM_QUINZENA_MSG } from "@/lib/equipe-errors";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SetorChipPicker } from "@/components/equipe/SetorChipPicker";
import { PagamentoAdicionalBlock } from "@/components/equipe/PagamentoAdicionalBlock";
import { formatSetores } from "@/lib/setores";

// ─── Types ────────────────────────────────────────────────────────────────────

export type TarefaStatus = "PENDENTE" | "EM_ANDAMENTO" | "CONCLUIDA" | "CANCELADA" | "FALTOU";
export type DiariaType = "NORMAL" | "MEIA_DIARIA" | "HORA_EXTRA" | "DESCONTO" | "ADIANTAMENTO" | "EMPREITA" | "MENSALISTA";

export interface TarefaParaEditar {
  id: string;
  data: string;
  funcionarioId: string;
  funcionarioNome: string;
  atividadeId: string;
  atividadeNome: string;
  setorId: string | null;
  setorIds?: string[];   // multi-setor (prefer over setorId when present)
  setorNome: string;
  tipo: string;
  status: TarefaStatus;
  valor: string;
  valorAdicional: string | null;
  comentario: string | null;
}

export interface DiariaParaEditar {
  id: string;
  quinzenaId: string;
  data: string;
  funcionarioId: string;
  funcionarioNome: string;
  tipo: string;
  valor: string;
  valorAdicional: string | null;
  comentario: string | null;
  tarefaId?: string | null;
  status?: TarefaStatus;
}

interface LinhaAtiv {
  setorIds: string[];      // multi-setor
  atividadeId: string;
  tarefaId?: string | null; // existing tarefa ID for reconciliation in edit-day mode
}

interface TarefaDiariaSheetProps {
  open: boolean;
  mode: "create" | "edit";
  // Create:
  dataInicial?: string;
  defaultFuncionarioId?: string;
  quinzenaId?: string;
  // Edit:
  tarefa?: TarefaParaEditar | null;
  diaria?: DiariaParaEditar | null;
  tarefasDoDia?: TarefaParaEditar[]; // all work tarefas of the day (enables day-editor mode)
  // Data:
  funcionarios: { id: string; nome: string; apelido?: string | null; valorDiaria?: string | null; tipoRemuneracao?: string | null }[];
  atividades: { id: string; nome: string }[];
  setores: { id: string; nome: string }[];
  onClose: () => void;
  onSuccess: () => void;
  initialStatus?: TarefaStatus;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_CONFIG: {
  value: TarefaStatus;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  border: string;
  activeBg: string;
  activeText: string;
}[] = [
  { value: "PENDENTE",     label: "A fazer",      Icon: Circle,       border: "border-border",  activeBg: "bg-muted",  activeText: "text-foreground"  },
  { value: "EM_ANDAMENTO", label: "Em andamento", Icon: Clock,        border: "border-atencao/30", activeBg: "bg-atencao-suave",  activeText: "text-atencao" },
  { value: "CONCLUIDA",    label: "Concluída",    Icon: CheckCircle2, border: "border-sucesso", activeBg: "bg-sucesso-suave",  activeText: "text-sucesso" },
  { value: "FALTOU",       label: "Faltou",       Icon: UserX,        border: "border-erro/30",   activeBg: "bg-erro-suave",    activeText: "text-erro"   },
];

const TIPO_OPTIONS: { value: DiariaType; label: string }[] = [
  { value: "NORMAL",       label: "Diária"       },
  { value: "MEIA_DIARIA",  label: "Meia Diária"  },
  { value: "HORA_EXTRA",   label: "Hora extra"   },
  { value: "EMPREITA",     label: "Empreita"     },
  { value: "MENSALISTA",   label: "Mensalista"   },
  { value: "DESCONTO",     label: "Desconto"     },
  { value: "ADIANTAMENTO", label: "Adiantamento" },
];

const emptyLinha = (): LinhaAtiv => ({ setorIds: [], atividadeId: "" });

// ─── Hook ─────────────────────────────────────────────────────────────────────

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ─── Shared style constants ───────────────────────────────────────────────────

const labelClass = "block text-xs font-medium text-muted-foreground mb-1";
const inputClass = "w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-card";
const selectClass = `${inputClass} appearance-none`;
const selectLinhaBase = "border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-card appearance-none";
const readonlyClass = "w-full border border-border rounded-lg px-3 py-2 text-sm bg-muted text-muted-foreground cursor-default";

// ─── Main component ───────────────────────────────────────────────────────────

export function TarefaDiariaSheet({
  open,
  mode,
  dataInicial,
  defaultFuncionarioId,
  quinzenaId,
  tarefa,
  diaria,
  tarefasDoDia,
  funcionarios,
  atividades,
  setores,
  onClose,
  onSuccess,
  initialStatus,
}: TarefaDiariaSheetProps) {
  const isMobile = useIsMobile();
  const qc = useQueryClient();
  const { toast } = useToast();

  // ── Form state ──
  const [data, setData] = useState("");
  const [funcionarioId, setFuncionarioId] = useState("");
  const [editSetorIds, setEditSetorIds] = useState<string[]>([]);
  const [editAtivId, setEditAtivId] = useState("");
  const [status, setStatus] = useState<TarefaStatus>("PENDENTE");
  const [tipo, setTipo] = useState<DiariaType>("NORMAL");
  const [valor, setValor] = useState("");
  const [comentario, setComentario] = useState("");
  const [linhas, setLinhas] = useState<LinhaAtiv[]>([emptyLinha()]);

  // FALTOU tracking
  const valorAnteriorRef = useRef<string>("0");
  const isFaltou = status === "FALTOU";
  const isMensalista = tipo === "MENSALISTA";

  // Regime do funcionário selecionado. O servidor recusa (409 REGIME_MENSALISTA /
  // REGIME_DIARISTA) tarefa com tipo incompatível — a tela nem oferece a opção errada:
  // mensalista só tem "Registro (sem valor)", diarista nunca tem.
  const funcSelecionado = funcionarios.find(f => f.id === funcionarioId);
  const funcEhMensalista = (funcSelecionado?.tipoRemuneracao ?? "DIARISTA") === "MENSALISTA";
  const tipoOptions = TIPO_OPTIONS.filter(o =>
    funcEhMensalista ? o.value === "MENSALISTA" : o.value !== "MENSALISTA"
  );

  // ── Derived mode flags ──
  const isCreateMode = mode === "create";
  const tarefasDoDiaKey = tarefasDoDia?.map(t => t.id).join(",") ?? "";
  const isEditDay = mode === "edit" && !!tarefasDoDia && tarefasDoDia.length > 0;
  const isEditTarefa = mode === "edit" && !!tarefa && !isEditDay;
  const isEditDiaria = mode === "edit" && !!diaria && !tarefa && !isEditDay;
  // Guarda: modo edit sem entidade alguma (estado transitório) → não renderizar formulário
  const isEditEmpty = mode === "edit" && !isEditDay && !isEditTarefa && !isEditDiaria;

  // A tarefa que ancora o adicional. O extra é pago por DIA trabalhado, não por
  // atividade — quando o dia tem várias tarefas, a âncora é só ponto de fixação:
  // `data` e `quinzena_id` do adicional vêm da tarefa, e todas as tarefas do dia
  // compartilham as duas. Trocar a âncora não mudaria valor nem quinzena.
  const tarefaAncora = isEditTarefa
    ? (tarefa?.id ? { id: tarefa.id, funcionarioId: tarefa.funcionarioId } : null)
    : isEditDay
    ? (tarefasDoDia?.[0] ? { id: tarefasDoDia[0].id, funcionarioId: tarefasDoDia[0].funcionarioId } : null)
    : isEditDiaria && diaria?.tarefaId
    ? { id: diaria.tarefaId, funcionarioId: diaria.funcionarioId }
    : null;

  // ── Reset state on open / entity change ──
  useEffect(() => {
    if (!open) return;

    if (isEditDay && tarefasDoDia) {
      // Sort: put portadora (highest valor) first
      const sorted = [...tarefasDoDia].sort(
        (a, b) => parseFloat(String(b.valor ?? 0)) - parseFloat(String(a.valor ?? 0))
      );
      setData(sorted[0].data);
      setFuncionarioId(sorted[0].funcionarioId);
      setStatus(sorted[0].status as TarefaStatus);
      setTipo((sorted[0].tipo as DiariaType) ?? "NORMAL");
      const valorPortadora = parseFloat(String(sorted[0].valor ?? 0));
      setValor(valorPortadora === 0 ? "" : String(valorPortadora));
      setComentario(sorted[0].comentario ?? "");
      setLinhas(sorted.map(t => ({
        setorIds: t.setorIds?.length ? t.setorIds : (t.setorId ? [t.setorId] : []),
        atividadeId: t.atividadeId ?? "",
        tarefaId: t.id,
      })));
      valorAnteriorRef.current = sorted[0].status === "FALTOU" ? String(sorted[0].valor ?? "0") : "0";
    } else if (mode === "edit" && tarefa) {
      setData(tarefa.data);
      setFuncionarioId(tarefa.funcionarioId);
      setEditSetorIds(tarefa.setorIds?.length ? tarefa.setorIds : (tarefa.setorId ? [tarefa.setorId] : []));
      setEditAtivId(tarefa.atividadeId);
      setStatus(initialStatus ?? tarefa.status);
      setTipo((tarefa.tipo as DiariaType) ?? "NORMAL");
      const valorParsed = parseFloat(String(tarefa.valor ?? 0));
      setValor(isNaN(valorParsed) || valorParsed === 0 ? "" : String(valorParsed));
      setComentario(tarefa.comentario ?? "");
      if (tarefa.status === "FALTOU") {
        valorAnteriorRef.current = String(tarefa.valor ?? "0");
      }
    } else if (mode === "edit" && diaria) {
      setData(diaria.data);
      setFuncionarioId(diaria.funcionarioId);
      setTipo((diaria.tipo as DiariaType) ?? "NORMAL");
      const valorParsed = parseFloat(String(diaria.valor));
      setValor(isNaN(valorParsed) ? "" : valorParsed.toFixed(2));
      setComentario(diaria.comentario ?? "");
      const initialStatus = diaria.status ?? "PENDENTE";
      setStatus(initialStatus);
      if (initialStatus === "FALTOU") {
        valorAnteriorRef.current = String(diaria.valor ?? "0");
      }
    } else {
      // create mode
      const today = format(new Date(), "yyyy-MM-dd");
      setData(dataInicial ?? today);
      setFuncionarioId(defaultFuncionarioId ?? "");
      setStatus("PENDENTE");
      const func = funcionarios.find(f => f.id === (defaultFuncionarioId ?? ""));
      // Abrir já no funcionário certo: mensalista nasce com o tipo de registro.
      const funcMensalista = (func?.tipoRemuneracao ?? "DIARISTA") === "MENSALISTA";
      setTipo(funcMensalista ? "MENSALISTA" : "NORMAL");
      setValor(funcMensalista ? "0" : (func?.valorDiaria ? String(parseFloat(func.valorDiaria)) : ""));
      setComentario("");
      setLinhas([emptyLinha()]);
      setEditSetorIds([]);
      valorAnteriorRef.current = "0";
    }
  }, [open, tarefa?.id, diaria?.id, tarefasDoDiaKey, mode]);

  // Block body scroll when sheet is open (mobile only)
  useEffect(() => {
    if (isMobile && open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [isMobile, open]);

  // Handle status change with FALTOU rule
  const handleStatusChange = (newStatus: TarefaStatus) => {
    if (newStatus === "FALTOU") {
      valorAnteriorRef.current = valor || "0";
      setValor("0");
    } else if (status === "FALTOU") {
      setValor(valorAnteriorRef.current === "0" ? "" : valorAnteriorRef.current);
    }
    setStatus(newStatus);
  };

  const handleSelectFunc = (id: string) => {
    setFuncionarioId(id);
    const func = funcionarios.find(f => f.id === id);
    // Trocar de funcionário troca o regime: alinhar tipo e valor na hora, senão o
    // save bateria no 409 do servidor com o formulário todo preenchido.
    if ((func?.tipoRemuneracao ?? "DIARISTA") === "MENSALISTA") {
      setTipo("MENSALISTA");
      setValor("0");
      return;
    }
    if (tipo === "MENSALISTA") setTipo("NORMAL");
    if (func?.valorDiaria) setValor(String(parseFloat(func.valorDiaria)));
  };

  // ── Linhas helpers ──
  const updateLinhaAtiv = (idx: number, atividadeId: string) =>
    setLinhas(ls => ls.map((l, i) => i === idx ? { ...l, atividadeId } : l));
  const updateLinhaSetorIds = (idx: number, setorIds: string[]) =>
    setLinhas(ls => ls.map((l, i) => i === idx ? { ...l, setorIds } : l));
  const addLinha = () => setLinhas(ls => [...ls, emptyLinha()]);
  const removeLinha = (idx: number) =>
    setLinhas(ls => ls.length > 1 ? ls.filter((_, i) => i !== idx) : ls);

  // ── Mutations ──

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["/api/equipe/tarefas"] });
    qc.invalidateQueries({ queryKey: ["/api/equipe/diarias"] });
  };

  // Resposta do servidor que carrega o veredito do consolidador (§3). Cada request
  // sobre o mesmo (funcionário, data, tipo) recomputa a diária lendo o grupo inteiro:
  // por isso os requests do dia rodam UM DE CADA VEZ — em paralelo, o último a
  // escrever pode ter lido antes das escritas dos outros e gravar uma soma velha,
  // em silêncio. São 2–4 requests por dia; no celular a diferença não se nota.
  type RespostaConsolidador = { diariaConsolidada?: boolean; avisoDiaria?: string } | null | undefined;
  const primeiraFalha = (respostas: RespostaConsolidador[]): RespostaConsolidador =>
    respostas.find(r => r && r.diariaConsolidada === false);

  const createMutation = useMutation({
    mutationFn: async (): Promise<RespostaConsolidador> => {
      const validas = linhas.filter(l => l.atividadeId);
      // FALTOU/MENSALISTA sem atividade selecionada: cria uma única tarefa sem atividade
      const toCreate = ((isFaltou || isMensalista) && validas.length === 0)
        ? [{ setorIds: [] as string[], atividadeId: "" }]
        : validas;
      const results: RespostaConsolidador[] = [];
      for (let i = 0; i < toCreate.length; i++) {
        const l = toCreate[i];
        const r = await apiRequest("POST", "/api/equipe/tarefas", {
          funcionarioId,
          atividadeId: l.atividadeId || null,
          setorIds: l.setorIds,
          setorId: l.setorIds[0] ?? null, // backward compat
          data,
          tipo,
          status,
          valor: isMensalista ? "0" : i === 0 ? (valor || "0") : "0", // MENSALISTA sempre 0
          valorAdicional: "0",
          comentario: comentario.trim() || null,
        });
        results.push(await r.json());
      }
      return primeiraFalha(results);
    },
    onSuccess: (falha) => {
      invalidateAll();
      toast({ title: isFaltou ? "Falta registrada!" : `${linhas.filter(linhaValida).length} tarefa(s) criada(s)!` });
      avisarSeDiariaFalhou(toast, falha);
      onSuccess();
    },
    onError: (err) => isSemQuinzena(err)
      ? toast({ title: "Crie a quinzena primeiro", description: SEM_QUINZENA_MSG, variant: "destructive" })
      : toast({ title: "Erro ao criar tarefa", variant: "destructive" }),
  });

  const saveTarefaMutation = useMutation({
    mutationFn: () => {
      const valorNum = parseFloat(valor);
      return apiRequest("PATCH", `/api/equipe/tarefas/${tarefa!.id}`, {
        data,
        funcionarioId,
        atividadeId: editAtivId || tarefa!.atividadeId,
        setorIds: editSetorIds,
        setorId: editSetorIds[0] ?? null, // backward compat
        tipo,
        status,
        // Só envia valor se for número > 0 — evita zerar a diária consolidada do dia
        // ao salvar uma tarefa companheira (valor 0). FALTOU é tratado via status no backend.
        ...(!isNaN(valorNum) && valorNum > 0 ? { valor: String(valorNum) } : {}),
        valorAdicional: "0",
        comentario: comentario.trim() || null,
      }).then(r => r.json());
    },
    onSuccess: () => {
      invalidateAll();
      toast({ title: "Tarefa atualizada!" });
      onSuccess();
    },
    onError: (err) => isSemQuinzena(err)
      ? toast({ title: "Crie a quinzena primeiro", description: SEM_QUINZENA_MSG, variant: "destructive" })
      : toast({ title: "Erro ao salvar tarefa", variant: "destructive" }),
  });

  const deleteTarefaMutation = useMutation({
    mutationFn: () => apiRequest("DELETE", `/api/equipe/tarefas/${tarefa!.id}`),
    onSuccess: () => {
      invalidateAll();
      toast({ title: "Tarefa excluída" });
      onSuccess();
    },
    onError: () => toast({ title: "Erro ao excluir tarefa", variant: "destructive" }),
  });

  const saveDiariaMutation = useMutation({
    mutationFn: async () => {
      // Patch the diária itself
      const patchedDiaria = await apiRequest("PATCH", `/api/equipe/diarias/${diaria!.id}`, {
        tipo,
        valor: valor || "0",
        valorAdicional: "0",
        comentario: comentario.trim() || null,
      }).then(r => r.json());

      // If diária is linked to a tarefa, also update its status
      if (diaria!.tarefaId) {
        await apiRequest("PATCH", `/api/equipe/tarefas/${diaria!.tarefaId}`, {
          status,
          valor: valor || "0",
          valorAdicional: "0",
          comentario: comentario.trim() || null,
        }).then(r => r.json());
      }

      return patchedDiaria;
    },
    onSuccess: () => {
      invalidateAll();
      toast({ title: "Diária atualizada!" });
      onSuccess();
    },
    onError: () => toast({ title: "Erro ao salvar diária", variant: "destructive" }),
  });

  const deleteDiariaMutation = useMutation({
    mutationFn: () => apiRequest("DELETE", `/api/equipe/diarias/${diaria!.id}`),
    onSuccess: () => {
      invalidateAll();
      toast({ title: "Lançamento removido" });
      onSuccess();
    },
    onError: () => toast({ title: "Erro ao excluir lançamento", variant: "destructive" }),
  });

  const saveEditDiaMutation = useMutation({
    mutationFn: async (): Promise<RespostaConsolidador> => {
      const resolvedValor = (isFaltou || isMensalista) ? "0" : (valor || "0");
      // Build valid lines: FALTOU may have no atividade
      const linhasValidas = isFaltou
        ? (linhas.some(l => l.atividadeId)
            ? linhas.filter(l => l.atividadeId)
            : [{ setorIds: [] as string[], atividadeId: "", tarefaId: tarefasDoDia![0].id }])
        : linhas.filter(l => l.atividadeId);
      if (linhasValidas.length === 0) return;

      const existingLinhas = linhasValidas.filter(l => l.tarefaId);
      const newLinhas = linhasValidas.filter(l => !l.tarefaId);

      // Tarefas that were removed from the list
      const keptIds = new Set(existingLinhas.map(l => l.tarefaId));
      const tarefasRemover = tarefasDoDia!.filter(t => !keptIds.has(t.id));

      const respostas: RespostaConsolidador[] = [];

      // 1) Remover PRIMEIRO. A regra do backend "última tarefa do dia remove a diária"
      //    não pode correr junto com o upsert do portador de valor — senão substituir
      //    todas as linhas do dia de uma vez pode zerar a diária (corrida).
      //    E remover é UMA chamada em lote, não N DELETEs em paralelo: cada DELETE
      //    recomputava a diária elegendo como carrier uma tarefa que o DELETE vizinho
      //    estava apagando (FK 23503, Sentry 7712783444). O lote apaga tudo e recomputa
      //    uma vez por grupo, depois do último delete.
      if (tarefasRemover.length > 0) {
        const r = await apiRequest("POST", "/api/equipe/tarefas/excluir-lote", {
          ids: tarefasRemover.map(t => t.id),
        });
        respostas.push(await r.json());
      }

      // 2) Depois aplicar PATCH (existentes) + POST (novas), um de cada vez (ver
      //    RespostaConsolidador). Primeira tarefa carrega o valor do dia; companheiras
      //    omitem valor no PATCH (backend só sincroniza diária com valor presente no body).
      for (let i = 0; i < existingLinhas.length; i++) {
        const l = existingLinhas[i];
        const r = await apiRequest("PATCH", `/api/equipe/tarefas/${l.tarefaId}`, {
          data,
          atividadeId: l.atividadeId || null,
          setorIds: l.setorIds,
          setorId: l.setorIds[0] ?? null, // backward compat
          tipo,
          status,
          ...(i === 0 ? { valor: resolvedValor } : {}), // companions: omitir valor
        });
        respostas.push(await r.json());
      }
      for (let i = 0; i < newLinhas.length; i++) {
        const l = newLinhas[i];
        const r = await apiRequest("POST", "/api/equipe/tarefas", {
          funcionarioId,
          atividadeId: l.atividadeId || null,
          setorIds: l.setorIds,
          setorId: l.setorIds[0] ?? null, // backward compat
          data,
          tipo,
          status,
          // POST com valor=0 não cria diária (guard no backend: valorNum > 0)
          valor: existingLinhas.length === 0 && i === 0 ? resolvedValor : "0",
        });
        respostas.push(await r.json());
      }
      return primeiraFalha(respostas);
    },
    onSuccess: (falha) => {
      invalidateAll();
      toast({ title: "Dia atualizado!" });
      avisarSeDiariaFalhou(toast, falha);
      onSuccess();
    },
    onError: (err) => isSemQuinzena(err)
      ? toast({ title: "Crie a quinzena primeiro", description: SEM_QUINZENA_MSG, variant: "destructive" })
      : toast({ title: "Erro ao salvar", variant: "destructive" }),
  });

  const deleteEditDiaMutation = useMutation({
    // Uma chamada em lote (não N DELETEs em paralelo — ver saveEditDiaMutation) e a
    // resposta é LIDA: antes o corpo era descartado e a falha do consolidador virava
    // "Dia excluído" com uma diária órfã por trás.
    mutationFn: async (): Promise<RespostaConsolidador> => {
      const r = await apiRequest("POST", "/api/equipe/tarefas/excluir-lote", {
        ids: tarefasDoDia!.map(t => t.id),
      });
      return r.json();
    },
    onSuccess: (falha) => {
      invalidateAll();
      toast({ title: "Dia excluído" });
      avisarSeDiariaFalhou(toast, falha);
      onSuccess();
    },
    onError: () => toast({ title: "Erro ao excluir", variant: "destructive" }),
  });

  const isPending =
    createMutation.isPending ||
    saveTarefaMutation.isPending ||
    deleteTarefaMutation.isPending ||
    saveDiariaMutation.isPending ||
    deleteDiariaMutation.isPending ||
    saveEditDiaMutation.isPending ||
    deleteEditDiaMutation.isPending;

  const handleDelete = () => {
    if (isEditDay) {
      const count = tarefasDoDia!.length;
      if (!window.confirm(`Excluir todas as ${count} tarefa(s) do dia?`)) return;
      deleteEditDiaMutation.mutate();
    } else if (tarefa) {
      if (!window.confirm("Excluir esta tarefa?")) return;
      deleteTarefaMutation.mutate();
    } else if (diaria) {
      if (!window.confirm("Excluir este lançamento?")) return;
      deleteDiariaMutation.mutate();
    }
  };

  const handleSave = () => {
    if (mode === "create") {
      createMutation.mutate();
    } else if (isEditDay) {
      saveEditDiaMutation.mutate();
    } else if (tarefa) {
      saveTarefaMutation.mutate();
    } else if (diaria) {
      saveDiariaMutation.mutate();
    }
  };

  // ── Content ──

  const title = isCreateMode
    ? "Nova Tarefa"
    : isEditDay
    ? "Editar dia"
    : isEditTarefa
    ? "Editar Tarefa"
    : "Editar Diária";

  const submitLabel = isCreateMode ? "Criar tarefa(s)" : isEditDay ? "Salvar dia" : "Salvar alterações";

  const formContent = (
    <div className="space-y-4">
      {/* Data */}
      <div>
        <label className={labelClass}>Data</label>
        {isEditDiaria ? (
          <div className={readonlyClass}>
            {diaria ? format(new Date(diaria.data + "T00:00:00"), "eee dd/MM", { locale: ptBR }) : ""}
          </div>
        ) : (
          <input
            type="date"
            value={data}
            onChange={e => setData(e.target.value)}
            className={inputClass}
          />
        )}
      </div>

      {/* Funcionário */}
      <div>
        <label className={labelClass}>Funcionário</label>
        {isEditDiaria || isEditDay ? (
          <div className={readonlyClass}>
            {isEditDiaria
              ? (diaria?.funcionarioNome ?? "")
              : (funcionarios.find(f => f.id === funcionarioId)?.apelido ??
                 funcionarios.find(f => f.id === funcionarioId)?.nome ?? "")}
          </div>
        ) : (
          <select
            value={funcionarioId}
            onChange={e => handleSelectFunc(e.target.value)}
            className={selectClass}
          >
            <option value="">Selecionar...</option>
            {funcionarios.map(f => (
              <option key={f.id} value={f.id}>{f.apelido ?? f.nome}</option>
            ))}
          </select>
        )}
      </div>

      {/* Edit tarefa: atividade + setores (multi) */}
      {isEditTarefa && (
        <>
          <div>
            <label className={labelClass}>Atividade</label>
            <select
              value={editAtivId}
              onChange={e => setEditAtivId(e.target.value)}
              className={selectClass}
            >
              <option value="">Selecionar...</option>
              {atividades.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
            </select>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={labelClass}>
                Setor(es)
                {editSetorIds.length > 0 && (
                  <span className="text-sucesso font-normal ml-1">— {formatSetores(editSetorIds, setores)}</span>
                )}
              </label>
            </div>
            <SetorChipPicker
              selectedIds={editSetorIds}
              onChange={setEditSetorIds}
              setores={setores}
              disabled={isPending}
            />
          </div>
        </>
      )}

      {/* §5.1 — Fase 3: pagamento adicional, exclusivo de tarefa EXISTENTE
          (precisa de tarefaId para o rateio referenciar). Recolhido por
          default, não é diária — soma só no total da quinzena. Vale para os
          3 modos de edição (tarefa, dia, diária) — só "Nova Tarefa" fica de
          fora, de propósito (sem tarefaId salvo, o adicional não tem onde
          se prender). */}
      {tarefaAncora && (
        <>
          {isEditDay && tarefasDoDia && tarefasDoDia.length > 1 && (
            <p className="text-xs text-muted-foreground -mb-2">
              o extra fica preso à tarefa: {tarefasDoDia[0].atividadeNome}
            </p>
          )}
          <PagamentoAdicionalBlock tarefaId={tarefaAncora.id} funcionarioIdDaTarefa={tarefaAncora.funcionarioId} data={data} />
        </>
      )}

      {/* Tipo */}
      <div>
        <label className={labelClass}>Tipo</label>
        <select
          value={tipo}
          onChange={e => {
            const newTipo = e.target.value as DiariaType;
            if (newTipo === "MENSALISTA") setValor("0");
            setTipo(newTipo);
          }}
          className={selectClass}
        >
          {tipoOptions.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Status grid — create, edit tarefa, and edit diária with linked tarefa */}
      {(!isEditDiaria || diaria?.tarefaId) && (
        <>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</p>
          <div className="grid grid-cols-2 gap-2">
            {STATUS_CONFIG.map(({ value: sv, label, Icon, border, activeBg, activeText }) => {
              const isActive = status === sv;
              return (
                <button
                  key={sv}
                  type="button"
                  onClick={() => handleStatusChange(sv)}
                  disabled={isPending}
                  className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2.5 text-sm font-medium transition-all ${
                    isActive
                      ? `${border} ${activeBg} ${activeText}`
                      : "border-border bg-muted text-muted-foreground hover:border-input"
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {label}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Create / edit-day mode: linhas atividade+setores */}
      {(isCreateMode || isEditDay) && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={labelClass}>
              Atividade · Setores
              {isFaltou && <span className="text-muted-foreground font-normal ml-1">opcional</span>}
            </label>
            <button
              type="button"
              onClick={addLinha}
              className="flex items-center gap-1 text-xs text-sucesso font-medium hover:text-sucesso"
            >
              <Plus className="h-3 w-3" />
              Adicionar linha
            </button>
          </div>
          <div className="space-y-4">
            {linhas.map((linha, idx) => {
              const setorLabel = formatSetores(linha.setorIds, setores);
              return (
                <div key={idx} className="border border-border rounded-xl p-3 space-y-2 relative">
                  {/* Remover linha */}
                  <button
                    type="button"
                    onClick={() => removeLinha(idx)}
                    disabled={linhas.length === 1}
                    className="absolute top-2 right-2 h-6 w-6 flex items-center justify-center text-muted-foreground hover:text-erro disabled:opacity-30"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                  {/* Atividade */}
                  <div>
                    <label className={labelClass}>Atividade{!isFaltou && " *"}</label>
                    <select
                      value={linha.atividadeId}
                      onChange={e => updateLinhaAtiv(idx, e.target.value)}
                      className={selectClass}
                    >
                      <option value="">{isFaltou ? "Atividade (opcional)" : "Selecionar atividade *"}</option>
                      {atividades.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
                    </select>
                  </div>
                  {/* Setores */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className={labelClass}>
                        Setor(es){!isFaltou && " *"}
                        {setorLabel && (
                          <span className="text-sucesso font-normal ml-1">— {setorLabel}</span>
                        )}
                      </label>
                    </div>
                    <SetorChipPicker
                      selectedIds={linha.setorIds}
                      onChange={ids => updateLinhaSetorIds(idx, ids)}
                      setores={setores}
                      disabled={isPending}
                    />
                    {!isFaltou && linha.atividadeId && linha.setorIds.length === 0 && (
                      <p className="text-xs text-erro mt-1">Selecione pelo menos um setor</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <hr className="border-border" />

      {/* Valor — o passo de valor SOME para mensalista: o registro vale sempre R$ 0
          e é o servidor que força isso (I2). Mostrar um campo desabilitado só
          sugeriria que existe um valor a decidir. */}
      {isMensalista ? (
        <div className="rounded-xl border border-info/30 bg-info-suave px-3 py-2.5">
          <p className="text-sm font-medium text-info">🔵 Registro (sem valor)</p>
          <p className="text-xs text-info mt-0.5">
            Mensalista: a tarefa registra o dia e o ciclo, mas não entra na folha da quinzena.
          </p>
        </div>
      ) : (
      <div>
        <label className={labelClass}>Valor da diária (do dia)</label>
        <input
          type="number"
          step="0.01"
          min="0"
          placeholder="0,00"
          value={valor}
          onChange={e => setValor(e.target.value)}
          disabled={isFaltou || isMensalista}
          className={`${inputClass} text-right disabled:bg-muted disabled:text-muted-foreground`}
        />
        {isFaltou && (
          <p className="text-xs text-erro mt-1">Valor zerado automaticamente — colaborador faltou</p>
        )}
        {!isFaltou && valor !== "" && (isNaN(parseFloat(valor)) || parseFloat(valor) <= 0) && (
          <p className="text-xs text-erro mt-1">Informe o valor da diária</p>
        )}
        {!isFaltou && valor === "" && (
          <p className="text-xs text-muted-foreground mt-1">Campo obrigatório — informe o valor</p>
        )}
      </div>
      )}

      {/* Comentário */}
      <div>
        <label className={labelClass}>
          Comentário
          <span className="text-muted-foreground font-normal ml-1">opcional</span>
        </label>
        <textarea
          rows={2}
          placeholder="Observação..."
          value={comentario}
          onChange={e => setComentario(e.target.value)}
          maxLength={300}
          className={`${inputClass} resize-none`}
        />
      </div>
    </div>
  );

  const valorNum = parseFloat(valor);
  // Linha válida: tem atividade E pelo menos 1 setor selecionado (exceto FALTOU/MENSALISTA que é opcional)
  const linhaValida = (l: LinhaAtiv) => !!l.atividadeId && (l.setorIds.length > 0 || isFaltou || isMensalista);
  const canSubmitCreate = !!funcionarioId && (
    isFaltou || isMensalista
      ? true  // atividade e valor opcionais quando FALTOU ou MENSALISTA
      : linhas.some(linhaValida) && valorNum > 0 && !isNaN(valorNum)
  );
  const canSubmitEditDay = !!funcionarioId && (
    isFaltou || isMensalista ? true : linhas.some(linhaValida) && valorNum > 0 && !isNaN(valorNum)
  );

  const footerContent = (
    <div className="flex gap-2 mt-4">
      {/* Delete button (edit mode only) */}
      {mode === "edit" && (
        <button
          type="button"
          onClick={handleDelete}
          disabled={isPending}
          className="h-10 w-10 shrink-0 flex items-center justify-center border-2 border-erro/30 text-erro hover:bg-erro-suave rounded-xl transition-colors disabled:opacity-50"
          aria-label="Excluir"
        >
          {deleteTarefaMutation.isPending || deleteDiariaMutation.isPending || deleteEditDiaMutation.isPending
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <Trash2 className="h-4 w-4" />}
        </button>
      )}
      <button
        type="button"
        onClick={handleSave}
        disabled={isPending || (isCreateMode && !canSubmitCreate) || (isEditDay && !canSubmitEditDay)}
        className="flex-1 bg-primary hover:opacity-90 disabled:opacity-50 text-primary-foreground rounded-xl py-2.5 text-sm font-medium transition-opacity flex items-center justify-center gap-2"
      >
        {(saveTarefaMutation.isPending || saveDiariaMutation.isPending || createMutation.isPending || saveEditDiaMutation.isPending) && (
          <Loader2 className="h-4 w-4 animate-spin" />
        )}
        {isPending ? "Salvando..." : submitLabel}
      </button>
    </div>
  );

  // ── Render: mobile = bottom sheet, desktop = Dialog ──

  if (!isMobile) {
    return (
      <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          {isEditEmpty
            ? <p className="text-sm text-muted-foreground py-4 text-center">Lançamento não encontrado.</p>
            : formContent}
          {!isEditEmpty && footerContent}
        </DialogContent>
      </Dialog>
    );
  }

  // Mobile bottom sheet
  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        className={`fixed bottom-0 left-0 right-0 bg-card rounded-t-2xl z-50 transition-transform duration-300 ease-out shadow-xl ${open ? "translate-y-0" : "translate-y-full"}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-9 h-1 rounded-full bg-border" />
        </div>

        <div className="px-5 pb-8 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-semibold text-foreground">{title}</h2>
            <button
              onClick={onClose}
              className="h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
              aria-label="Fechar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {isEditEmpty
            ? <p className="text-sm text-muted-foreground py-4 text-center">Lançamento não encontrado.</p>
            : formContent}
          {!isEditEmpty && footerContent}
        </div>
      </div>
    </>
  );
}
